import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { Loader2, Send, ArrowLeft, Sparkles, MapPin } from "lucide-react";
import { useAuth } from "@/lib/auth";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function Chat() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [openers, setOpeners] = useState(null);
  const [showOpeners, setShowOpeners] = useState(false);
  const [loadingOpeners, setLoadingOpeners] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/matches/${matchId}/messages`);
      setThread(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load conversation"));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matchId]);

  const send = async (payloadText) => {
    const trimmed = (payloadText ?? text).trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const { data: msg } = await api.post(`/matches/${matchId}/messages`, { text: trimmed });
      setThread((t) => ({ ...t, messages: [...(t?.messages || []), msg] }));
      setText("");
      setShowOpeners(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      toast.error(errorMessage(err, "Could not send"));
    } finally {
      setSending(false);
    }
  };

  const loadIcebreakers = async () => {
    setShowOpeners(true);
    if (openers) return;
    setLoadingOpeners(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/icebreakers`);
      setOpeners(data.openers || []);
    } catch (err) {
      toast.error(errorMessage(err, "Could not generate icebreakers"));
      setShowOpeners(false);
    } finally {
      setLoadingOpeners(false);
    }
  };

  if (!thread) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
      </div>
    );
  }

  const other = thread.other_user;
  const otherPhoto = other.photos?.[0];

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-10 py-6 md:py-10">
      <div className="flex items-center gap-4 pb-4 border-b border-[#e5e0d8]">
        <Link to="/inbox" className="p-2 rounded-full hover:bg-[#eae5d9]" data-testid="chat-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="w-12 h-12 rounded-full overflow-hidden bg-[#eae5d9]">
          {otherPhoto?.data_url && <img src={otherPhoto.data_url} alt={other.display_name} className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-serif-display text-2xl text-[#722f37]" data-testid="chat-other-name">
              {other.display_name}, <span className="text-[#5c5c5c]">{other.age}</span>
            </h2>
            {other.verified && <VerifiedBadge size={12} />}
          </div>
          {other.location && (
            <div className="text-xs text-[#5c5c5c] flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" /> {other.location}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-3 min-h-[45vh]" data-testid="chat-messages">
        {thread.messages.length === 0 && (
          <div className="text-center text-[#5c5c5c] py-12">
            <Sparkles className="w-6 h-6 mx-auto text-[#c39d2b]" />
            <p className="mt-3">You matched. Start the conversation.</p>
          </div>
        )}
        {thread.messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
              <div
                className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-base leading-relaxed ${
                  mine
                    ? "bg-[#722f37] text-white rounded-br-sm"
                    : "bg-white border border-[#e5e0d8] text-[#1a1a1a] rounded-bl-sm"
                }`}
              >
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {showOpeners && (
        <div className="mt-4 p-4 rounded-2xl bg-[#faf9f6] border border-[#e5e0d8]" data-testid="chat-openers">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#722f37]">Suggested openers</span>
            <button onClick={() => setShowOpeners(false)} className="text-xs text-[#5c5c5c] hover:text-[#722f37]">Hide</button>
          </div>
          {loadingOpeners ? (
            <div className="flex items-center gap-2 text-[#5c5c5c] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Writing openers…</div>
          ) : (
            <div className="space-y-2">
              {openers?.map((o, i) => (
                <button
                  key={i}
                  onClick={() => send(o)}
                  className="w-full text-left p-3 rounded-xl bg-white border border-[#e5e0d8] hover:border-[#722f37] transition-colors text-sm"
                  data-testid={`chat-opener-${i}`}
                >
                  {o}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="mt-4 sticky bottom-4 flex items-center gap-2"
        data-testid="chat-composer"
      >
        <button
          type="button"
          onClick={loadIcebreakers}
          className="rounded-full border border-[#722f37] text-[#722f37] p-3 hover:bg-[#722f37] hover:text-white transition-colors"
          title="Suggest openers"
          data-testid="chat-icebreakers-btn"
        >
          <Sparkles className="w-5 h-5" />
        </button>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a message…"
          className="flex-1 h-14 px-4 rounded-full border border-[#d6cfc0] focus:border-[#722f37] focus:ring-2 focus:ring-[#722f37]/20 outline-none text-base bg-white"
          data-testid="chat-input"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          className="btn-primary !py-3 !px-5 flex items-center gap-2"
          data-testid="chat-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
